import { fabric } from 'fabric';
import BaseHandler from './BaseHandler';
import { SCALE_FACTOR } from '../common/constants';
import {
  boxOverflow,
  boxRole,
  isFixedHeight,
  textBoxSettings,
  validateTextBox,
  withShrinkToBox,
  withTextBox,
} from 'helpers/textBox';
import { stackIdOf, validateStack } from 'helpers/stacks';

/** How the overflow of each kind of fixed box is drawn (see boxRole). */
const OVERFLOW = {
  // shrunk to fit at render
  shrink: { color: '#b7791f', note: ' · shrinks' },
  // pushes the stack members below; the stack indicator's colour
  reserve: { color: '#e8590c', note: ' · pushes' },
  // runs past the box over whatever is below
  fixed: { color: '#e53e3e', note: '' },
};
const TAG_FONT = '600 11px sans-serif';
const TAG_HEIGHT = 16;
const TAG_PADDING = 5;

/**
 * Fixed text boxes on the canvas (rules in helpers/textBox).
 *
 * Writes a text's box settings, and draws, on every fixed box whose text runs
 * past its bottom, a line along the box's bottom edge with how far the text
 * reaches: amber when the stitcher will shrink it to fit, orange when it will
 * push the stack members below, red when it will bleed. Nothing is shrunk or
 * pushed on the canvas; the server preview shows the result.
 */
class TextBoxHandler extends BaseHandler {
  constructor(props) {
    super(props);
    this.canvas.on('after:render', this.drawIndicators);
  }

  isText = object => object instanceof fabric.Textbox;

  settingsOf = object => textBoxSettings(object?.metadata);

  /** The box height in template pixels: the size_y the export will save. */
  boxHeight = object =>
    Math.round((object.height * object.scaleY) / SCALE_FACTOR);

  /** How far the text on the canvas reaches past its fixed box, in template px. */
  overflowOf = object => Math.round(boxOverflow(object) / SCALE_FACTOR);

  /**
   * Writes the box settings onto a text. Fixing the height keeps the box
   * where it is, for the designer to drag; releasing it fits the box to its
   * text again.
   */
  updateSettings = (object, settings) => {
    if (!this.isText(object)) return { error: 'Only a text has a text box' };

    const metadata = withTextBox(object.metadata, settings);
    const errors = validateTextBox(metadata);
    if (Object.keys(errors).length) return { errors };

    this.write(object, metadata);
    this.commit();
    return { ok: true };
  };

  /**
   * Sets how a text shrinks when it renders too long, with its floor:
   * "box" shrinks it to fit its own box, which fixes the box height; "stack"
   * shrinks it when its stack runs out of room; "off" never shrinks it. In a
   * stack the member's stack keys follow (see StacksHandler.setMemberShrink).
   * One history step.
   */
  setShrink = (object, mode, minFontSize) => {
    if (!this.isText(object)) return { error: 'Only a text has a text box' };
    const inStack = stackIdOf(object) !== null;
    if (mode === 'stack' && !inStack) {
      return { error: 'The text is not in a stack' };
    }

    // leaving "box" releases the height, except in a stack, where it is kept
    // as the reserved height
    const settings = withShrinkToBox(
      textBoxSettings(object.metadata),
      mode === 'box',
      inStack
    );
    if (mode === 'box') settings.box_min_font_size = minFontSize;
    const metadata = withTextBox(object.metadata, settings);
    const errors = validateTextBox(metadata);
    if (Object.keys(errors).length) return { errors };
    if (mode === 'stack') {
      const stackErrors = validateStack({
        id: 'x',
        min_font_size: minFontSize,
      });
      if (stackErrors.min_font_size) {
        return { errors: { min_font_size: stackErrors.min_font_size } };
      }
    }

    this.write(object, metadata);
    if (inStack) {
      const stacked = this.handlers.stacksHandler.setMemberShrink(
        object,
        mode === 'stack',
        minFontSize,
        { commit: false }
      );
      if (!stacked.ok) return stacked;
    }
    this.commit();
    return { ok: true };
  };

  /**
   * Replaces the metadata (never edited in place: duplicate hands the copy
   * the original's metadata object), re-fitting a box that is no longer fixed.
   */
  write = (object, metadata) => {
    const wasFixed = isFixedHeight(object);
    object.set('metadata', metadata);
    if (wasFixed && !isFixedHeight(object)) object.initDimensions();
    object.setCoords();
    object.dirty = true;
  };

  commit = () => {
    this.canvas.requestRenderAll();
    this.handlers.historyHandler.save('textbox:updated');
    this.editor.emit('textbox:changed', {});
    this.editor.emit('stacks:changed', { type: 'textbox:updated' });
  };

  drawIndicators = () => {
    const ctx = this.canvas.getSelectionContext();
    this.canvas.getObjects().forEach(object => {
      const over = boxOverflow(object);
      if (over < 1) return;
      this.drawOverflow(ctx, object, over);
    });
  };

  drawOverflow = (ctx, object, over) => {
    // the bottom edge through the object's full transform and the viewport, so
    // it sits right on rotated objects and inside a multi-selection
    const matrix = fabric.util.multiplyTransformMatrices(
      this.canvas.viewportTransform,
      object.calcTransformMatrix()
    );
    const w = object.width / 2;
    const h = object.height / 2;
    const [start, end] = [
      [-w, h],
      [w, h],
    ].map(([x, y]) =>
      fabric.util.transformPoint(new fabric.Point(x, y), matrix)
    );
    const { color, note } = OVERFLOW[boxRole(object)];
    const label = `${Math.round(over / SCALE_FACTOR)} px over${note}`;

    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();

    // a tag hanging under the right end of the edge
    ctx.font = TAG_FONT;
    ctx.textBaseline = 'middle';
    const width = ctx.measureText(label).width + TAG_PADDING * 2;
    const left = Math.max(start.x, end.x) - width;
    const top = Math.max(start.y, end.y) + 1;
    ctx.fillStyle = color;
    ctx.fillRect(left, top, width, TAG_HEIGHT);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(label, left + TAG_PADDING, top + TAG_HEIGHT / 2 + 0.5);
    ctx.restore();
  };
}

export default TextBoxHandler;
